import React from 'react';
import { EmptySearchState } from 'shared-portal-components-v2';

import type {
  ExternalUser,
  ExternalUserSortDir,
  ExternalUserSortKey,
} from '@/types/external-users.types';

import ExternalUsersDesktopRow from './ExternalUsersDesktopRow';
import ExternalUsersMobileRow from './ExternalUsersMobileRow';
import ExternalUsersTableHeader from './ExternalUsersTableHeader';

interface ExternalUsersTableProps {
  users: ExternalUser[];
  sortKey: ExternalUserSortKey;
  sortDir: ExternalUserSortDir;
  onSortChange: (key: ExternalUserSortKey) => void;
  onDeleteRequest: (user: ExternalUser) => void;
  showEmptyState: boolean;
  showEmptySearchState: boolean;
}

function ExternalUsersTable({
  users,
  sortKey,
  sortDir,
  onSortChange,
  onDeleteRequest,
  showEmptyState,
  showEmptySearchState,
}: ExternalUsersTableProps): React.ReactElement {
  if (showEmptyState) {
    return (
      <div
        className="rounded-lg border border-border py-12 text-center"
        data-testid="external-users-empty"
      >
        <p className="font-medium text-foreground">No external users yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          External users are added by inviting them while creating a SIX or managing SIX access.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border" data-testid="external-users-table">
      <ExternalUsersTableHeader sortKey={sortKey} sortDir={sortDir} onSort={onSortChange} />
      {showEmptySearchState ? (
        <EmptySearchState
          title="No external users found"
          description="We couldn't find any external users matching your search. Try adjusting your filters or search query."
        />
      ) : (
        users.map((user) => (
          <React.Fragment key={user.id}>
            <ExternalUsersDesktopRow user={user} onDeleteRequest={onDeleteRequest} />
            <ExternalUsersMobileRow user={user} onDeleteRequest={onDeleteRequest} />
          </React.Fragment>
        ))
      )}
    </div>
  );
}

export default ExternalUsersTable;
